import * as THREE from 'three';

/**
 * Orthografische camera op een vaste isometrische hoek.
 *
 * De hoek ligt vast — geen vrije rotatie — omdat de hele art direction op één leesrichting
 * is ontworpen (zie docs/ART_DIRECTION.md). De camera volgt de speler met exponentiële
 * smoothing en een dead zone, zodat kleine bewegingen het beeld niet laten meedeinen.
 */

/** Ware isometrie: azimut 45°, elevatie atan(1/√2) ≈ 35.264°. Richting = normalize(1,1,1). */
export const ISO_YAW = Math.PI / 4;
export const ISO_PITCH = Math.atan(1 / Math.SQRT2);

/**
 * Hoeveel schermhoogte een wereld-verticale meter beslaat onder deze camerahoek.
 * Gelijk aan cos(pitch) = √(2/3) ≈ 0.8165 — een staande figuur wordt verkort weergegeven.
 */
export const VERTICAL_FORESHORTENING = Math.cos(ISO_PITCH);

/**
 * Afstand van de camera tot het volgpunt. Bij een orthografische camera heeft dit geen
 * invloed op de projectie, maar wel op near/far én op de mist: die wordt gemeten vanaf de
 * camera, dus mistgrenzen moeten hier omheen liggen (zie `fogRange`).
 */
export const CAMERA_DISTANCE = 90;

/**
 * Mistbereik dat als afstand-tot-het-focuspunt leest.
 *
 * Alles rond de speler zit op ongeveer CAMERA_DISTANCE van de camera; door de mist daar
 * omheen te leggen vervaagt de stad naar de randen van het beeld in plaats van in één klap.
 */
export function fogRange(): { near: number; far: number } {
  return { near: CAMERA_DISTANCE + 6, far: CAMERA_DISTANCE + 62 };
}

/**
 * Zichtbare wereldhoogte in meters bij gameplay-zoom. Op een scherm van ~900 px is Luca
 * daarmee ongeveer 55 px hoog: klein genoeg om de straat te overzien, groot genoeg om zijn
 * silhouet te herkennen. De asset-review-zoom (200 px) zit in de debug-overlay.
 */
const DEFAULT_VIEW_HEIGHT = 34;

/** Binnen deze straal rond het huidige focuspunt beweegt de camera niet mee. */
const DEAD_ZONE = 0.35;

/** Aandeel van de resterende afstand dat per seconde wordt weggewerkt (exponentiële smoothing). */
const FOLLOW_RESPONSE = 0.999;

/**
 * Schudden na een klap.
 *
 * Trauma in plaats van amplitude: `shake()` telt trauma op en de uitslag volgt daar niet
 * lineair uit, zodat een aanrijding domineert zonder dat je per gebeurtenis een aparte sterkte
 * hoeft te verzinnen.
 *
 * De exponent stond eerst op 2. Dat wiste kleine gebeurtenissen volledig uit: een pistoolschot
 * kwam gemeten uit op 0.014 m, oftewel een vijfde pixel op speelafstand. Met 1.5 blijft het
 * onderscheid tussen tik en dreun intact terwijl de tik ook echt zichtbaar is.
 */
export const CAMERA_SHAKE = {
  /** Maximale uitslag in meters bij vol trauma. */
  maxOffset: 0.9,
  /** Hoeveel trauma per seconde wegzakt. */
  decayPerSecond: 2.6,
  /** Trillingen per seconde. Hoog genoeg om als schok te lezen, niet als deining. */
  frequency: 26,
  /** Zie hierboven: 2 was te hard voor de kleine gebeurtenissen. */
  falloffExponent: 1.5,
} as const;

export class IsoCamera {
  readonly camera: THREE.OrthographicCamera;

  /** Punt waar de camera op is gericht; volgt het doel met smoothing. */
  readonly focus = new THREE.Vector3();

  #viewHeight = DEFAULT_VIEW_HEIGHT;
  #aspect = 1;
  #trauma = 0;
  #shakeTime = 0;
  readonly #shakeOffset = new THREE.Vector3();

  /** Eenheidsvector van focuspunt naar camera. */
  readonly #offsetDir = new THREE.Vector3(1, 1, 1).normalize();

  constructor() {
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, CAMERA_DISTANCE * 4);
    this.camera.name = 'NA_IsoCamera';
    this.#apply();
  }

  /** Zichtbare wereldhoogte in meters. Kleiner = verder ingezoomd. */
  get viewHeight(): number {
    return this.#viewHeight;
  }

  set viewHeight(meters: number) {
    this.#viewHeight = Math.max(2, meters);
    this.#applyFrustum();
  }

  /**
   * Zet de zoom zo dat een figuur van `characterHeight` meter precies `pixels` hoog
   * op het scherm staat. De art bible toetst assets op ±200 px bij 1.855 m.
   */
  setCharacterPixelHeight(pixels: number, viewportHeightPx: number, characterHeight = 1.855): void {
    const screenMeters = characterHeight * VERTICAL_FORESHORTENING;
    this.viewHeight = (screenMeters * viewportHeightPx) / pixels;
  }

  resize(width: number, height: number): void {
    this.#aspect = width / height;
    this.#applyFrustum();
  }

  /** Huidig trauma, 0..1. Vooral om op te toetsen. */
  get trauma(): number {
    return this.#trauma;
  }

  /** Voeg trauma toe. Stapelt op tot 1, zodat een salvo niet oneindig doorschudt. */
  shake(strength: number): void {
    if (!Number.isFinite(strength) || strength <= 0) return;
    this.#trauma = Math.min(1, this.#trauma + strength);
  }

  /**
   * Laat het schudden verlopen. Los van `follow`, want tijdens een cutscene stuurt de
   * cutscene de camera en zou het trauma anders blijven hangen.
   */
  update(dt: number): void {
    if (this.#trauma <= 0) {
      if (this.#shakeOffset.lengthSq() > 0) {
        this.#shakeOffset.set(0, 0, 0);
        this.#apply();
      }
      return;
    }

    this.#trauma = Math.max(0, this.#trauma - CAMERA_SHAKE.decayPerSecond * dt);
    this.#shakeTime += dt;

    const amplitude = Math.pow(this.#trauma, CAMERA_SHAKE.falloffExponent) * CAMERA_SHAKE.maxOffset;
    const phase = this.#shakeTime * CAMERA_SHAKE.frequency;
    // Twee onderling priemachtige frequenties, zodat het niet als een nette cirkel leest.
    this.#shakeOffset.set(Math.sin(phase) * amplitude, 0, Math.cos(phase * 1.37) * amplitude);
    this.#apply();
  }

  /** Zet het focuspunt direct, zonder smoothing. Gebruik bij spawn en scene-wissels. */
  snapTo(target: THREE.Vector3): void {
    this.focus.copy(target);
    this.#apply();
  }

  /**
   * Volg `target` met smoothing. Framerate-onafhankelijk: de vaste stap uit de loop komt
   * binnen als `dt`, en de responsfactor wordt daarop geschaald.
   */
  follow(target: THREE.Vector3, dt: number, options: { readonly response?: number; readonly deadZone?: number } = {}): void {
    const distance = this.focus.distanceTo(target);
    const deadZone = options.deadZone ?? DEAD_ZONE;
    const response = options.response ?? FOLLOW_RESPONSE;
    if (distance > deadZone) {
      const t = 1 - Math.pow(1 - response, dt);
      // Trek het doel de dead zone in in plaats van er precies op te gaan staan; dat
      // voorkomt dat de camera bij stilstaan blijft natrillen.
      const pull = ((distance - deadZone) / distance) * t;
      this.focus.lerp(target, pull);
    }
    this.#apply();
  }

  #applyFrustum(): void {
    const halfHeight = this.#viewHeight / 2;
    const halfWidth = halfHeight * this.#aspect;
    this.camera.left = -halfWidth;
    this.camera.right = halfWidth;
    this.camera.top = halfHeight;
    this.camera.bottom = -halfHeight;
    this.camera.updateProjectionMatrix();
  }

  #apply(): void {
    this.camera.position.copy(this.focus).addScaledVector(this.#offsetDir, CAMERA_DISTANCE);
    this.camera.lookAt(this.focus);
    // De schudbeweging komt ná lookAt: hij verschuift het beeld zonder de kijkrichting of het
    // focuspunt aan te tasten, zodat `follow` en de cutscenes er niets van merken.
    this.camera.position.add(this.#shakeOffset);
    this.camera.updateMatrixWorld();
  }
}
